// src/components/Trading/AutoSchedulerTabEnhanced.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Grid,
  Chip,
  IconButton,
  LinearProgress,
  Alert,
  Collapse,
  Fade,
  Zoom,
  Slide,
  Badge,
  Tooltip,
  CircularProgress,
  Divider,
  alpha,
  useTheme,
  Card,
  CardContent,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
  Schedule as ScheduleIcon,
  Notifications as BellIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Speed as SpeedIcon,
  Timer as TimerIcon,
  TrendingUp as TrendingUpIcon,
  Bolt as BoltIcon,
  AutoAwesome as SparkleIcon,
  Rocket as RocketIcon,
  Timeline as TimelineIcon,
  SignalCellularAlt as SignalIcon,
  Circle as DotIcon,
  ArrowForward as ArrowIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

// Import the notification system
import NotificationSystem from './NotificationSystem';

const AutoSchedulerTabEnhanced = ({ apiBaseUrl = window.location.origin }) => {
  const theme = useTheme();
  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [countdown, setCountdown] = useState(null);
  const [logs, setLogs] = useState([]);
  const [wsConnected, setWsConnected] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [stats, setStats] = useState({
    totalSignals: 0,
    successfulEntries: 0,
    failedSignals: 0,
    activeStrategies: 0,  // Change back to number for simplicity
  });
  const [activeSignals, setActiveSignals] = useState([]);
  const [broadcastResults, setBroadcastResults] = useState([]);
  
  const wsRef = useRef(null);
  const intervalRef = useRef(null);
  const notificationIdRef = useRef(0);

  // Test sequence for CobraLong strategy
  const testSequence = [
    { 
      signal: 'LUX_SHORT_ACTIVE', 
      timeframe: '5m',
      delay: 0,
      description: 'Trigger early signal',
      expectedResult: 'Waiting for confirmation',
      icon: <TrendingUpIcon />,
      color: '#ffa502',
    },
    { 
      signal: null,
      delay: 60,
      description: 'Waiting period (min_minutes threshold)',
      expectedResult: 'Time window active',
      icon: <TimerIcon />,
      color: '#00d4ff',
    },
    { 
      signal: 'COBRA_LONG_ACTIVE',
      timeframe: '5m',
      delay: 120,
      description: 'Send confirmation signal',
      expectedResult: '✅ ENTRY CONFIRMED',
      icon: <CheckIcon />,
      color: '#00ff88',
    },
    { 
      signal: null,
      delay: 180,
      description: 'Hold position',
      expectedResult: 'In position',
      icon: <SignalIcon />,
      color: '#00d4ff',
    },
    { 
      signal: 'LUX_SHORT_ACTIVE',
      timeframe: '5m',
      delay: 240,
      description: 'Send exit signal',
      expectedResult: 'Position closed',
      icon: <StopIcon />,
      color: '#ff4757',
    },
  ];

  // Add notification
  const addNotification = useCallback((type, title, message, data = {}) => {
    const id = `notif-${++notificationIdRef.current}-${Date.now()}`;
    const notification = {
      id,
      type,
      title,
      message,
      timestamp: new Date().toISOString(),
      ...data
    };
    setNotifications(prev => [...prev, notification]);
    return id;
  }, []);

  // Remove notification
  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  // Connect to WebSocket
  useEffect(() => {
    connectWebSocket();
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const connectWebSocket = () => {
    try {
      const ws = new WebSocket(`ws://${apiBaseUrl.replace('http://', '')}/ws/strategy-alerts/`);
      
      ws.onopen = () => {
        setWsConnected(true);
        addLog('success', '🔌 WebSocket connected');
        addNotification('success', 'Connected', 'WebSocket connection established');
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          handleWebSocketMessage(data);
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error);
        }
      };

      ws.onerror = (error) => {
        addLog('error', `❌ WebSocket error: ${error.message}`);
        addNotification('error', 'Connection Error', 'WebSocket connection failed');
      };

      ws.onclose = () => {
        setWsConnected(false);
        addLog('warning', '🔌 WebSocket disconnected');
        addNotification('warning', 'Disconnected', 'WebSocket connection lost');
        
        // Attempt reconnection after 5 seconds
        setTimeout(connectWebSocket, 5000);
      };

      wsRef.current = ws;
    } catch (error) {
      console.error('Failed to connect WebSocket:', error);
      addLog('error', `Failed to connect: ${error.message}`);
    }
  };

  const handleWebSocketMessage = (data) => {
    const { signal_type, message, strategy_id, signal, price, timestamp } = data;
    
    // Update stats
    setStats(prev => ({
      ...prev,
      totalSignals: prev.totalSignals + 1,
    }));

    // Add to active signals
    setActiveSignals(prev => [{
      id: `signal-${Date.now()}`,
      type: signal_type,
      strategy: strategy_id,
      signal,
      price,
      timestamp,
      message
    }, ...prev].slice(0, 10)); // Keep only last 10

    // Handle different signal types
    switch (signal_type) {
      case 'entry':
        addLog('success', `✅ ${message}`);
        addNotification('success', 'Entry Signal', message, {
          strategy: strategy_id,
          price,
          signal
        });
        setStats(prev => ({
          ...prev,
          successfulEntries: prev.successfulEntries + 1,
        }));
        break;
        
      case 'exit':
        addLog('info', `🚪 ${message}`);
        addNotification('info', 'Exit Signal', message, {
          strategy: strategy_id,
          price,
          signal
        });
        break;
        
      case 'early':
        addLog('warning', `⏳ ${message}`);
        addNotification('warning', 'Early Signal', message, {
          strategy: strategy_id,
          signal
        });
        break;
        
      case 'broadcast':
        addLog('info', `📡 ${message}`);
        if (data.results) {
          setBroadcastResults(data.results);
        }
        break;
        
      default:
        addLog('info', message);
    }
  };

  const addLog = (type, message) => {
    const timestamp = new Date().toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      fractionalSecondDigits: 3
    });
    
    setLogs(prev => [{
      id: `log-${Date.now()}-${Math.random()}`,
      type,
      message,
      timestamp
    }, ...prev].slice(0, 100)); // Keep only last 100 logs
  };

  const startScheduler = async () => {
    setIsRunning(true);
    setIsPaused(false);
    setCurrentStep(0);
    setLogs([]);
    setActiveSignals([]);
    setBroadcastResults([]);
    setStats({
      totalSignals: 0,
      successfulEntries: 0,
      failedSignals: 0,
      activeStrategies: 0,
    });
    
    addLog('info', '🚀 Starting automated test sequence...');
    addNotification('info', 'Scheduler Started', 'Automated test sequence initiated');
    
    // Setup strategy first
    await setupStrategy();
    
    // Run test sequence
    runTestSequence();
  };

  const setupStrategy = async () => {
    try {
      addLog('info', '⚙️ Setting up CobraLong strategy...');
      
      const response = await fetch(`${apiBaseUrl}/strategies/api/strategy/setup/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          strategy_id: 'CobraLong',
          symbol: 'BTCUSD',
          timeframe: '5m',
          early_signal: 'LUX_SHORT_ACTIVE',
          confirmation_signal: 'COBRA_LONG_ACTIVE',
          exit_signal: 'LUX_SHORT_ACTIVE',
          min_minutes: 1,
          max_minutes: 10
        })
      });

      if (response.ok) {
        const data = await response.json();
        addLog('success', '✅ Strategy configured successfully');
        setStats(prev => ({
          ...prev,
          activeStrategies: prev.activeStrategies + 1,
        }));
      } else {
        throw new Error('Failed to setup strategy');
      }
    } catch (error) {
      addLog('error', `❌ Setup failed: ${error.message}`);
      setIsRunning(false);
    }
  };

  const runTestSequence = () => {
    let stepIndex = 0;
    
    const executeStep = async () => {
      if (!isRunning || stepIndex >= testSequence.length) {
        setIsRunning(false);
        setCountdown(null);
        addLog('success', '✅ Test sequence completed');
        addNotification('success', 'Complete', 'Test sequence finished successfully');
        return;
      }

      if (isPaused) {
        return;
      }

      const step = testSequence[stepIndex];
      setCurrentStep(stepIndex);

      if (step.signal) {
        await sendSignal(step.signal, step.timeframe);
      }

      // Set countdown for next step
      if (stepIndex < testSequence.length - 1) {
        const nextStep = testSequence[stepIndex + 1];
        let remainingTime = nextStep.delay - step.delay;
        
        const countdownInterval = setInterval(() => {
          if (!isRunning || isPaused) {
            clearInterval(countdownInterval);
            return;
          }
          
          remainingTime--;
          setCountdown(remainingTime);
          
          if (remainingTime <= 0) {
            clearInterval(countdownInterval);
            stepIndex++;
            executeStep();
          }
        }, 1000);
      } else {
        stepIndex++;
        setTimeout(executeStep, 1000);
      }
    };

    executeStep();
  };

  const sendSignal = async (signal, timeframe) => {
    try {
      addLog('info', `📤 Broadcasting signal: ${signal} @ ${timeframe}`);
      
      const response = await fetch(`${apiBaseUrl}/strategies/api/signal/broadcast/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          signal,
          timeframe,
          price: 50000 + Math.random() * 1000, // Mock price
          timestamp: new Date().toISOString()
        })
      });

      if (response.ok) {
        const data = await response.json();
        addLog('success', `✅ Signal broadcasted to ${data.matched_strategies} strategies`);
      } else {
        throw new Error('Failed to send signal');
      }
    } catch (error) {
      addLog('error', `❌ Failed to send signal: ${error.message}`);
      setStats(prev => ({
        ...prev,
        failedSignals: prev.failedSignals + 1,
      }));
    }
  };

  const pauseScheduler = () => {
    setIsPaused(!isPaused);
    addLog('warning', isPaused ? '▶️ Resumed' : '⏸️ Paused');
  };

  const stopScheduler = () => {
    setIsRunning(false);
    setIsPaused(false);
    setCurrentStep(0);
    setCountdown(null);
    addLog('info', '⏹️ Scheduler stopped');
    addNotification('info', 'Stopped', 'Scheduler has been stopped');
  };

  const formatTime = (seconds) => {
    if (!seconds || seconds < 0) return '00:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getLogIcon = (type) => {
    switch(type) {
      case 'success': return <CheckIcon sx={{ fontSize: 14, color: '#00ff88' }} />;
      case 'error': return <ErrorIcon sx={{ fontSize: 14, color: '#ff4757' }} />;
      case 'warning': return <WarningIcon sx={{ fontSize: 14, color: '#ffa502' }} />;
      default: return <DotIcon sx={{ fontSize: 8, color: '#00d4ff' }} />;
    }
  };

  return (
    <Box sx={{ p: 3, position: 'relative' }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          background: 'linear-gradient(135deg, rgba(15, 20, 40, 0.95) 0%, rgba(20, 25, 45, 0.95) 100%)',
          backdropFilter: 'blur(20px)',
          border: '1px solid',
          borderColor: alpha('#00d4ff', 0.2),
          borderRadius: 3,
        }}
      >
        <Grid container alignItems="center" spacing={3}>
          <Grid item xs={12} md={6}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #00d4ff 0%, #0066ff 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 8px 32px rgba(0, 212, 255, 0.3)',
                }}
              >
                <ScheduleIcon sx={{ fontSize: 28, color: '#fff' }} />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700 }}>
                  Auto Scheduler
                </Typography>
                <Typography variant="body2" sx={{ color: alpha('#00d4ff', 0.7) }}>
                  Automated strategy testing with real-time signals
                </Typography>
              </Box>
            </Stack>
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack direction="row" spacing={2} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
              {/* Connection Status */}
              <Chip
                icon={wsConnected ? <DotIcon /> : <DotIcon />}
                label={wsConnected ? 'Connected' : 'Disconnected'}
                sx={{
                  bgcolor: wsConnected ? alpha('#00ff88', 0.2) : alpha('#ff4757', 0.2),
                  color: wsConnected ? '#00ff88' : '#ff4757',
                  borderColor: wsConnected ? '#00ff88' : '#ff4757',
                  '& .MuiChip-icon': {
                    color: wsConnected ? '#00ff88' : '#ff4757',
                    animation: wsConnected ? 'pulse 2s infinite' : 'none',
                  }
                }}
              />
              
              {/* Control Buttons */}
              {!isRunning ? (
                <Button
                  variant="contained"
                  startIcon={<PlayIcon />}
                  onClick={startScheduler}
                  sx={{
                    background: 'linear-gradient(135deg, #00ff88 0%, #00d4ff 100%)',
                    color: '#000',
                    fontWeight: 700,
                    px: 3,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #00d470 0%, #00a8cc 100%)',
                    }
                  }}
                >
                  Start Scheduler
                </Button>
              ) : (
                <Stack direction="row" spacing={1}>
                  <IconButton
                    onClick={pauseScheduler}
                    sx={{
                      bgcolor: alpha('#ffa502', 0.2),
                      color: '#ffa502',
                      '&:hover': { bgcolor: alpha('#ffa502', 0.3) }
                    }}
                  >
                    {isPaused ? <PlayIcon /> : <PauseIcon />}
                  </IconButton>
                  <IconButton
                    onClick={stopScheduler}
                    sx={{
                      bgcolor: alpha('#ff4757', 0.2),
                      color: '#ff4757',
                      '&:hover': { bgcolor: alpha('#ff4757', 0.3) }
                    }}
                  >
                    <StopIcon />
                  </IconButton>
                </Stack>
              )}
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total Signals', value: stats?.totalSignals || 0, color: '#00d4ff', icon: <SignalIcon /> },
          { label: 'Successful Entries', value: stats?.successfulEntries || 0, color: '#00ff88', icon: <CheckIcon /> },
          { label: 'Failed Signals', value: stats?.failedSignals || 0, color: '#ff4757', icon: <ErrorIcon /> },
          { label: 'Active Strategies', value: stats?.activeStrategies || 0, color: '#ffa502', icon: <RocketIcon /> },
        ].map((stat, index) => (
          <Grid item xs={6} md={3} key={`stat-${index}`}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card
                sx={{
                  background: alpha(stat.color || '#00d4ff', 0.1),
                  backdropFilter: 'blur(20px)',
                  border: '1px solid',
                  borderColor: alpha(stat.color || '#00d4ff', 0.3),
                  borderRadius: 2,
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="caption" sx={{ color: alpha('#fff', 0.6) }}>
                        {stat.label || ''}
                      </Typography>
                      <Typography variant="h4" sx={{ color: stat.color || '#00d4ff', fontWeight: 700 }}>
                        {stat.value || 0}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 1.5,
                        bgcolor: alpha(stat.color || '#00d4ff', 0.2),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {stat.icon && React.cloneElement(stat.icon, { sx: { color: stat.color || '#00d4ff' } })}
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        ))}
      </Grid>

      {/* Main Content */}
      <Grid container spacing={3}>
        {/* Test Sequence Progress */}
        <Grid item xs={12} lg={6}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              background: alpha('#0f1419', 0.8),
              backdropFilter: 'blur(20px)',
              border: '1px solid',
              borderColor: alpha('#00d4ff', 0.1),
              borderRadius: 2,
              height: '100%',
            }}
          >
            <Typography variant="h6" sx={{ color: '#fff', mb: 3, fontWeight: 600 }}>
              Test Sequence Progress
            </Typography>
            
            {/* Countdown Display */}
            {isRunning && countdown !== null && (
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  background: alpha('#00d4ff', 0.1),
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: alpha('#00d4ff', 0.3),
                }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="body2" sx={{ color: alpha('#fff', 0.7) }}>
                    Next step in:
                  </Typography>
                  <Typography variant="h4" sx={{ color: '#00d4ff', fontFamily: 'monospace', fontWeight: 700 }}>
                    {formatTime(countdown)}
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={100 - (countdown / 60) * 100}
                  sx={{
                    mt: 2,
                    height: 6,
                    borderRadius: 3,
                    bgcolor: alpha('#00d4ff', 0.1),
                    '& .MuiLinearProgress-bar': {
                      bgcolor: '#00d4ff',
                      borderRadius: 3,
                    }
                  }}
                />
              </Box>
            )}
            
            {/* Steps */}
            <Stack spacing={2}>
              {testSequence.map((step, index) => {
                const isActive = currentStep === index;
                const isCompleted = currentStep > index;
                const isPending = currentStep < index;
                
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: isActive 
                          ? alpha(step.color, 0.5)
                          : isCompleted
                            ? alpha('#00ff88', 0.3)
                            : alpha('#333', 0.5),
                        background: isActive
                          ? alpha(step.color, 0.1)
                          : isCompleted
                            ? alpha('#00ff88', 0.05)
                            : 'transparent',
                        opacity: isPending ? 0.5 : 1,
                        transition: 'all 0.3s ease',
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: 1,
                            bgcolor: isActive
                              ? alpha(step.color, 0.2)
                              : isCompleted
                                ? alpha('#00ff88', 0.2)
                                : alpha('#333', 0.3),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {isCompleted ? (
                            <CheckIcon sx={{ fontSize: 18, color: '#00ff88' }} />
                          ) : (
                            React.cloneElement(step.icon, { 
                              sx: { 
                                fontSize: 18, 
                                color: isActive ? step.color : '#666' 
                              } 
                            })
                          )}
                        </Box>
                        <Box flex={1}>
                          <Typography variant="body2" sx={{ color: '#fff', fontWeight: 600 }}>
                            {step.description}
                          </Typography>
                          {step.signal && (
                            <Typography variant="caption" sx={{ color: alpha('#fff', 0.5) }}>
                              Signal: {step.signal}
                            </Typography>
                          )}
                        </Box>
                        {isActive && (
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: step.color,
                              animation: 'pulse 1s infinite',
                            }}
                          />
                        )}
                      </Stack>
                    </Box>
                  </motion.div>
                );
              })}
            </Stack>
          </Paper>
        </Grid>

        {/* Active Signals & Logs */}
        <Grid item xs={12} lg={6}>
          <Stack spacing={3}>
            {/* Active Signals */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                background: alpha('#0f1419', 0.8),
                backdropFilter: 'blur(20px)',
                border: '1px solid',
                borderColor: alpha('#00d4ff', 0.1),
                borderRadius: 2,
              }}
            >
              <Typography variant="h6" sx={{ color: '#fff', mb: 2, fontWeight: 600 }}>
                Active Signals
              </Typography>
              
              <Stack spacing={1} sx={{ maxHeight: 200, overflowY: 'auto' }}>
                <AnimatePresence>
                  {activeSignals.length === 0 ? (
                    <Typography variant="body2" sx={{ color: alpha('#fff', 0.3), textAlign: 'center', py: 2 }}>
                      No active signals yet
                    </Typography>
                  ) : (
                    activeSignals.map((signal, index) => (
                      <motion.div
                        key={signal.id || `signal-${index}`}
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <Box
                          sx={{
                            p: 1.5,
                            borderRadius: 1,
                            bgcolor: alpha(
                              signal.type === 'entry' ? '#00ff88' :
                              signal.type === 'exit' ? '#ff4757' :
                              signal.type === 'early' ? '#ffa502' :
                              '#00d4ff',
                              0.1
                            ),
                            border: '1px solid',
                            borderColor: alpha(
                              signal.type === 'entry' ? '#00ff88' :
                              signal.type === 'exit' ? '#ff4757' :
                              signal.type === 'early' ? '#ffa502' :
                              '#00d4ff',
                              0.3
                            ),
                          }}
                        >
                          <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Chip
                                label={(signal.type || 'unknown').toUpperCase()}
                                size="small"
                                sx={{
                                  bgcolor: alpha(
                                    signal.type === 'entry' ? '#00ff88' :
                                    signal.type === 'exit' ? '#ff4757' :
                                    signal.type === 'early' ? '#ffa502' :
                                    '#00d4ff',
                                    0.2
                                  ),
                                  color: signal.type === 'entry' ? '#00ff88' :
                                        signal.type === 'exit' ? '#ff4757' :
                                        signal.type === 'early' ? '#ffa502' :
                                        '#00d4ff',
                                  fontSize: '0.7rem',
                                  fontWeight: 700,
                                  height: 20,
                                }}
                              />
                              <Typography variant="caption" sx={{ color: '#fff' }}>
                                {signal.strategy || 'Unknown'}
                              </Typography>
                            </Stack>
                            {signal.price && (
                              <Typography variant="caption" sx={{ color: '#00d4ff', fontFamily: 'monospace' }}>
                                ${signal.price.toFixed(2)}
                              </Typography>
                            )}
                          </Stack>
                          {signal.message && (
                            <Typography variant="caption" sx={{ color: alpha('#fff', 0.6), mt: 0.5, display: 'block' }}>
                              {signal.message}
                            </Typography>
                          )}
                        </Box>
                      </motion.div>
                    ))
                  )}
                </AnimatePresence>
              </Stack>
            </Paper>

            {/* Activity Log */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                background: alpha('#0f1419', 0.8),
                backdropFilter: 'blur(20px)',
                border: '1px solid',
                borderColor: alpha('#00d4ff', 0.1),
                borderRadius: 2,
              }}
            >
              <Typography variant="h6" sx={{ color: '#fff', mb: 2, fontWeight: 600 }}>
                Activity Log
              </Typography>
              
              <Stack
                spacing={0.5}
                sx={{
                  maxHeight: 300,
                  overflowY: 'auto',
                  '&::-webkit-scrollbar': {
                    width: 6,
                  },
                  '&::-webkit-scrollbar-thumb': {
                    bgcolor: alpha('#00d4ff', 0.3),
                    borderRadius: 3,
                  },
                }}
              >
                <AnimatePresence>
                  {logs.map((log, index) => (
                    <motion.div
                      key={log.id || `log-${index}`}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Stack
                        direction="row"
                        alignItems="flex-start"
                        spacing={1}
                        sx={{
                          py: 0.5,
                          px: 1,
                          '&:hover': {
                            bgcolor: alpha('#00d4ff', 0.05),
                            borderRadius: 1,
                          }
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: alpha('#00d4ff', 0.6),
                            fontFamily: 'monospace',
                            fontSize: '0.7rem',
                            minWidth: 70,
                          }}
                        >
                          {log.timestamp || ''}
                        </Typography>
                        {getLogIcon(log.type)}
                        <Typography
                          variant="caption"
                          sx={{
                            color: log.type === 'success' ? '#00ff88' :
                                  log.type === 'error' ? '#ff4757' :
                                  log.type === 'warning' ? '#ffa502' :
                                  alpha('#fff', 0.7),
                            fontSize: '0.75rem',
                            wordBreak: 'break-word',
                          }}
                        >
                          {log.message || ''}
                        </Typography>
                      </Stack>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </Stack>
            </Paper>
          </Stack>
        </Grid>
      </Grid>

      {/* Notification System */}
      <NotificationSystem
        notifications={notifications}
        onRemove={removeNotification}
      />

      {/* Global Animations */}
      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}
      </style>
    </Box>
  );
};

export default AutoSchedulerTabEnhanced;
